import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { LocationPicker } from '../locations/LocationPicker';
import { postProduction } from '../stock/api';
import type { ComponentBrief } from '../components/api';
import type { SpecVersion } from './api';

/** A completed BOM is a warehouse operation, not an edit to the BOM. */
export function ProductionForm({
  version,
  output,
}: {
  version: SpecVersion;
  output: ComponentBrief | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<'assembly' | 'disassembly'>('assembly');
  const [quantity, setQuantity] = useState('1');
  const [source, setSource] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState<number | null>(null);

  const post = useMutation({
    mutationFn: () =>
      postProduction({
        specification_version_id: version.id,
        operation,
        quantity,
        source_location_id: source as string,
        target_location_id: target as string,
        note: note || null,
      }),
    onSuccess: (result) => {
      setError(null);
      setPosted(result.movements.length);
      // Clear the fulfilled form: a second click cannot silently post the same
      // production run, and the next operation starts as an intentional choice.
      setQuantity('');
      setSource(null);
      setTarget(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
      void queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  if (version.status !== 'released') {
    return (
      <section className="panel__section spec__production">
        <h3>{t('specs.production.title')}</h3>
        <p className="panel__hint">{t('specs.production.versionNotReleased')}</p>
      </section>
    );
  }

  if (!output) {
    return (
      <section className="panel__section spec__production">
        <h3>{t('specs.production.title')}</h3>
        <p className="panel__hint">{t('specs.production.noOutput')}</p>
      </section>
    );
  }

  return (
    <section className="panel__section spec__production">
      <h3>{t('specs.production.title')}</h3>
      <p className="panel__hint">
        {t('specs.production.result')}: <strong>{output.name}</strong>
      </p>
      <form
        className="panel__form"
        onSubmit={(event) => {
          event.preventDefault();
          post.mutate();
        }}
      >
        <label>
          {t('specs.production.operation')}
          <select
            value={operation}
            onChange={(event) => {
              setPosted(null);
              setOperation(event.target.value as typeof operation);
            }}
          >
            <option value="assembly">{t('specs.production.assembly')}</option>
            <option value="disassembly">{t('specs.production.disassembly')}</option>
          </select>
        </label>
        <label>
          {t('specs.quantity')}
          <input
            value={quantity}
            inputMode="decimal"
            onChange={(event) => {
              setPosted(null);
              setQuantity(event.target.value.replace(',', '.'));
            }}
            required
          />
        </label>
        <label>
          {t('specs.production.source')}
          <LocationPicker
            label={t('specs.production.source')}
            value={source}
            onChange={(value) => {
              setPosted(null);
              setSource(value);
            }}
            allowOutside={false}
          />
        </label>
        <label>
          {t('specs.production.target')}
          <LocationPicker
            label={t('specs.production.target')}
            value={target}
            onChange={(value) => {
              setPosted(null);
              setTarget(value);
            }}
            allowOutside={false}
          />
        </label>
        <label>
          {t('specs.lineNote')}
          <input
            value={note}
            onChange={(event) => {
              setPosted(null);
              setNote(event.target.value);
            }}
          />
        </label>
        {error && <p className="panel__error">{t(`specs.errors.${error}`, t('specs.errors.unknown'))}</p>}
        {posted !== null && <p className="stock__ok">{t('specs.production.posted', { n: posted })}</p>}
        <div className="panel__actions">
          <button type="submit" disabled={!source || !target || !quantity || Number(quantity) <= 0 || post.isPending}>
            {operation === 'assembly' ? t('specs.production.assemble') : t('specs.production.disassemble')}
          </button>
        </div>
      </form>
    </section>
  );
}